<template>
  <div
    v-if="isLendflowUser"
    v-tooltip="t('COMBINATION.SEND_EMAIL')"
    class="flex h-4 w-4 items-center justify-center rounded-full bg-disabled hover:bg-primary-background-active cursor-pointer"
    data-cy="send-email-action"
    @click.stop="openEmailModal"
  >
    <icon-base
      :icon="IconEmail"
      width="14"
      height="14"
      view-box="0 0 16 16"
      data-cy="send-email-icon"
      class="text-label transition-colors hover:text-primary"
    />
  </div>
  <send-deal-communication-modal
    v-if="showEmailModal"
    :deal-id="activeDealId"
    :selected-email="email"
    @close-dialog="closeEmailModal"
  />
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useAuth } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useApplicationEmailsQuery } from "@/hooks/queries/applications";
import useEmailsStore from "@/stores/emails";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import IconEmail from "@/components/icons/IconEmail.vue";
import IconBase from "@/components/ui/IconBase.vue";

interface Props {
  email: string;
  dealId?: string;
}

const { dealId } = defineProps<Props>();

const { t } = useI18n();
const { activeDeal } = useDealsBase();
const { isLendflowUser } = useAuth();
const { showMessage } = useNotification();

const showEmailModal = ref(false);

const activeDealId = computed(() => dealId || activeDeal.value?.id || "");

const isAllEmailsQueryEnabled = computed(
  () => !!activeDealId.value && showEmailModal.value
);

const emailsStore = useEmailsStore();

const { data: emailsData } = useApplicationEmailsQuery({
  appId: activeDealId,
  enabled: isAllEmailsQueryEnabled,
});

const openEmailModal = () => {
  if (!activeDealId.value) {
    showMessage(t("ERRORS.EMAIL.NO_DEAL"), "error");
    return;
  }
  emailsStore.applicationEmails = emailsData.value;
  showEmailModal.value = true;
};

const closeEmailModal = () => {
  showEmailModal.value = false;
  emailsStore.applicationEmails = undefined;
};

watch(emailsData, (updatedEmails) => {
  if (activeDealId.value && showEmailModal.value) {
    emailsStore.applicationEmails = updatedEmails;
  }
});
</script>
